import { Flex, theme } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { CardContent, CardShellEcho } from '@/components/cards/common';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useAgePlaysQuery } from '@/services/revenueApi';
import type { AgePlaysResponse, SelectedPlatform } from '@/types/api/age';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';
import AgeModalEcho from './AgeModalEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';
import { AgeChartEcho } from './charts/AgeChart/AgeChartEcho';

const AgeCardEcho = ({ className }: { className?: string }) => {
  const isBeforeDataAvailability = useIsBeforeDataAvailability('age');
  const { queryResult } = useFetchQuery(useAgePlaysQuery, undefined, { sources: [] });
  const { data: agePlays } = queryResult;

  const platforms = useMemo(
    () => [...new Set(agePlays?.plays?.map((play) => play.platform) ?? [])],
    [agePlays]
  );

  type PlatformSelection = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<PlatformSelection | null>(
    platforms[0] ?? null
  );

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({
        plays: platforms.map((platform) => ({ platform })),
      })
    );
  }, [platforms]);

  const handlePlatformChange = (platform: PlatformSelection) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.AgeDemographics,
      platform,
      demographics: 'age',
    });
  };

  return (
    <CardShellEcho
      className={className}
      title="Age"
      headerOptions={agePlays?.plays?.length && <AgeModalEcho />}
    >
      {isBeforeDataAvailability ? (
        <DataAvailabilityBanner dataType="age" />
      ) : (
        <CardContent
          variant="echo"
          {...queryResult}
          renderData={(ageData: AgePlaysResponse) => (
            <Data
              data={ageData}
              selectedPlatform={selectedPlatform}
              handlePlatformChange={handlePlatformChange}
            />
          )}
        />
      )}
    </CardShellEcho>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: AgePlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const { token } = theme.useToken();

  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessageEcho dataType="age data" />;
  }

  return (
    <Flex
      vertical
      justify="space-between"
      gap={token.margin}
      style={{
        width: '100%',
        flex: 1,
        marginTop: token.marginLG,
      }}
    >
      <AgeChartEcho
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
    </Flex>
  );
};

export default AgeCardEcho;
